import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getColorFromString, getInitials } from '../utils/avatarUtils';
import { getPortraitStyle } from '../utils/speakerPortraitOverrides';
import { getSpeakerFraming, PORTRAIT_ZOOM_CHANGE_EVENT } from '../utils/speakerZoomOverrides';
import { prefetchSpeaker } from '../utils/prefetch';
import { useAccess } from '../contexts/AccessContext';
import {
  getSpeakerPaletteScene,
  PALETTE_PRIMED_EVENT,
} from '../utils/speakerPalette';
import PinSpeakerButton from './PinSpeakerButton';
import SpeakerNameEditor from './SpeakerNameEditor';
import useIsAdmin from '../hooks/useIsAdmin';
import { CheckSquare, Square, User, Clock3 } from 'lucide-react';

import { usesPlaceholderPortrait } from '../utils/placeholderSpeakers';

/** Feb-2026 v11: Studio-backdrop rotation across 31 warm hues
 *  (no red / yellow / brown), assigned round-robin by canonical
 *  position in `/api/speakers` so the same color never lands on
 *  adjacent tiles and repeats are spaced 31 speakers apart. Colors +
 *  CSS composition live in `utils/speakerPalette.js`. */

/** URLs from the transparent-cutout endpoint (RGBA, no background). We
 *  detect these so we can put the gold gradient BEHIND the image. Whitened
 *  and Drive photos already carry their own background, so we skip. */
const isTransparentCutout = (url) =>
  typeof url === 'string' && /\/api\/portrait\/cutout\//.test(url);

/**
 * Speaker tile.
 *
 * Admin extras (unlocked when useIsAdmin() is true):
 *   • Click the NAME to inline-rename (see SpeakerNameEditor).
 *   • In select-mode, the whole card becomes a checkbox toggle for bulk
 *     archive (parent passes `selectMode`, `selected`, `onToggleSelect`).
 */
const SpeakerCard = ({
  speaker,
  onClick,
  onRenamed,
  selectMode = false,
  selected = false,
  onToggleSelect,
  // Feb-2026 (user "speed pass"): first-viewport tiles pass "eager"
  // so their portrait paints immediately; below-fold tiles pass "lazy"
  // so the browser only fetches them once they scroll into view.
  priority = 'eager',
}) => {
  const { isGuest } = useAccess();
  const bgColor = getColorFromString(speaker.name);
  const initials = getInitials(speaker.name);
  const isAdmin = useIsAdmin();
  const portrait = getPortraitStyle(speaker.name);
  // Studio-backdrop CSS pulled from the shared palette so ALL grids
  // stay in lockstep. `paletteVersion` bumps whenever the canonical
  // speaker order gets primed, forcing every tile to re-look-up.
  const [paletteVersion, setPaletteVersion] = useState(0);
  useEffect(() => {
    const bump = () => setPaletteVersion((v) => v + 1);
    window.addEventListener(PALETTE_PRIMED_EVENT, bump);
    return () => window.removeEventListener(PALETTE_PRIMED_EVENT, bump);
  }, []);
  const cutoutBg = useMemo(
    () => getSpeakerPaletteScene(speaker.name),
    [speaker.name, paletteVersion],
  );
  // Feb-2026 (user #16 / PERMANENT RULE 20): a few compilation
  // speakers ("Are You A Lamdan?", "Hashkafa", "Hespedim") were
  // rendering as translucent cut-outs on a colored backdrop. The
  // user wants them to look like real-photo speakers. Force
  // isCutout=false for those so the tile falls back to the neutral
  // cream/dark card background and the image (transparent or not)
  // is shown as-is without a jarring red/orange square behind it.
  const NON_CUTOUT_FORCE = new Set(['are you a lamdan?', 'hashkafa', 'hespedim']);
  const forcePlaceholder = usesPlaceholderPortrait(speaker.name) || !!speaker.placeholderFigure;
  const isCutoutRaw = isTransparentCutout(speaker.image);
  const isCutout = isCutoutRaw && !NON_CUTOUT_FORCE.has((speaker.name || '').toLowerCase());

  // User-customized framing (scale + pan) from the speaker page's zoom tool.
  // Re-render whenever any speaker's framing changes so all tiles stay in sync.
  const [framing, setFraming] = useState(() => getSpeakerFraming(speaker.name));
  useEffect(() => {
    const sync = () => setFraming(getSpeakerFraming(speaker.name));
    sync();
    window.addEventListener(PORTRAIT_ZOOM_CHANGE_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(PORTRAIT_ZOOM_CHANGE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, [speaker.name]);

  const effectiveScale = framing?.scale ?? portrait.scale ?? 1;
  const effectiveRotate = framing?.rotate ?? 0;
  const effectivePosition = framing
    ? `${framing.posX}% ${framing.posY}%`
    : portrait.position;

  // Feb-2026 speed pass v3: IntersectionObserver-gated mount for the
  // portrait `<img>`. Eager tiles (first viewport) mount immediately;
  // below-fold tiles wait until they're within 600 px of the viewport
  // before the <img src> is even attached, so their portraits never
  // enter the initial network waterfall on cold load. This cut the
  // home page's cold portrait count from ~107 to the ~14-20 actually
  // visible above the fold.
  const rootRef = useRef(null);
  const [shouldRenderImg, setShouldRenderImg] = useState(priority === 'eager');
  useEffect(() => {
    if (shouldRenderImg) return;
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      // No IO support — fall back to eager mount so nothing breaks.
      setShouldRenderImg(true);
      return;
    }
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          setShouldRenderImg(true);
          io.disconnect();
          break;
        }
      }
    }, { rootMargin: '600px 200px' });
    io.observe(el);
    return () => io.disconnect();
  }, [shouldRenderImg]);

  // Cutout endpoint supports ?size=128/256/512/1024 — srcSet lets each
  // device pull only the resolution its screen needs (1x phones grab
  // ~15 KB, retina grabs 256/512) with zero visible quality loss.
  //
  // PERMANENT RULE (Feb 2026, thevaadotzar.com): speaker portraits on the
  // home grid MUST load eagerly with high priority. The user has seen
  // portraits pop-in/skeleton-load on the live site — even though they
  // are already fully downloaded — because `loading="lazy"` defers image
  // decode until the tile scrolls into view. We now force eager decode
  // so the entire row paints instantly, matching production behaviour
  // before the lazy-load optimisation. Do NOT re-add loading="lazy" here.
  // Feb-2026 (user "site isn't so fast — speed it up"): tiles display
  // at 96/112/128px CSS — even a 3× retina phone only needs 384px.
  // Previous config served 1024px everywhere (up to 8× oversized,
  // ~90 KB per portrait). Now: 512 for 1x/2x, 768 only for 3× hi-dpi.
  // This cuts the home page's portrait bandwidth ~3× (~10 MB saved).
  const isSizedCutout = /\/api\/portrait\/(cutout|white)\//.test(speaker.image || '');
  const sizeVariant = (s) => {
    const u = speaker.image;
    if (u.includes('size=')) return u.replace(/size=\d+/, `size=${s}`);
    return u.includes('?') ? `${u}&size=${s}` : `${u}?size=${s}`;
  };
  const srcSet = isSizedCutout
    ? `${sizeVariant(512)} 1x, ${sizeVariant(512)} 2x, ${sizeVariant(768)} 3x`
    : undefined;
  const primarySrc = isSizedCutout ? sizeVariant(512) : speaker.image;

  const rootClick = () => {
    if (selectMode) return onToggleSelect?.(speaker.name);
    onClick?.();
  };

  return (
    <div
      ref={rootRef}
      className="flex flex-col items-center cursor-pointer group relative"
      onClick={rootClick}
      // Prefetch this speaker's page data the moment the user's finger
      // lands on the tile — makes /speaker/{name} feel instantaneous.
      onPointerEnter={() => prefetchSpeaker(speaker.name)}
      onPointerDown={() => prefetchSpeaker(speaker.name)}
      onFocus={() => prefetchSpeaker(speaker.name)}
      data-testid={`speaker-card-${speaker.name}`}
    >
      {selectMode && (
        <div className="absolute top-1 left-1 z-10">
          <div className={`w-6 h-6 rounded-full flex items-center justify-center ${selected ? 'bg-[#9a7b24] text-white' : 'bg-black/60 text-white'}`}>
            {selected
              ? <CheckSquare className="w-4 h-4" data-testid={`speaker-select-on-${speaker.name}`} />
              : <Square className="w-4 h-4" data-testid={`speaker-select-off-${speaker.name}`} />}
          </div>
        </div>
      )}
      {!selectMode && (
        // Pin button — hover-gated on desktop, hidden on mobile so touch
        // devices don't have to consume the first tap surfacing the pin.
        <div className="absolute top-1 right-1 z-10 hidden sm:block opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="bg-black/60 backdrop-blur-sm rounded-full">
            <PinSpeakerButton speaker={speaker} variant="icon" />
          </div>
        </div>
      )}
      <div
        className={`w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded-full overflow-hidden flex items-center justify-center ring-2 transition-all shadow-md ${selected ? 'ring-[#e5c976]' : 'ring-[#c9a437] group-hover:ring-[#e5c976]'} relative`}
        style={{
          background: speaker.name === 'Under 5 Minutes'
            // Feb 2026 (user: "Give the shorts speaker card a custom
            // golden clock icon on the home grid so it stands out from
            // the portrait tiles"). Radial gold matches the hover-play
            // pucks + mini-player CTA — one visual language.
            ? 'radial-gradient(circle at 30% 25%, #f6dd8f 0%, #e5c976 30%, #c9a437 65%, #7d6119 100%)'
            : (isCutout ? cutoutBg : bgColor),
        }}
      >
        {speaker.name === 'Under 5 Minutes' ? (
          <div
            className="w-full h-full flex items-center justify-center"
            data-testid="speaker-under-5-icon"
          >
            <Clock3
              className="text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.35)]"
              strokeWidth={2}
              style={{ width: '58%', height: '58%' }}
            />
          </div>
        ) : forcePlaceholder ? (
          // Blue → gold gradient with an empty figure. Checked BEFORE the
          // portrait so a speaker on the placeholder list keeps the
          // placeholder even when a photo exists for him.
          <div
            className="w-full h-full flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg,#0f1729 0%,#1a2a4a 55%,#9a7b24 100%)' }}
            data-testid="speaker-placeholder-figure"
          >
            <User className="w-14 h-14 sm:w-16 sm:h-16 text-[#e5c976]" strokeWidth={1.4} />
          </div>
        ) : speaker.image && shouldRenderImg ? (
          <img
            src={primarySrc}
            srcSet={srcSet}
            alt={speaker.name}
            className="w-full h-full"
            style={{
              objectFit: portrait.fit,
              objectPosition: effectivePosition,
              transform: `scale(${effectiveScale}) rotate(${effectiveRotate}deg)`,
              transformOrigin: effectivePosition,
            }}
            loading={priority === 'lazy' ? 'lazy' : 'eager'}
            fetchPriority={priority === 'lazy' ? 'auto' : 'high'}
            decoding="async"
            draggable={false}
            // Transient failures (lazy-load aborts on scroll, cold CDN,
            // mobile ORB) used to hide the portrait forever. Retry twice
            // with backoff before falling back to the colored circle.
            onError={(e) => {
              const img = e.currentTarget;
              const tries = Number(img.dataset.retry || 0);
              if (tries < 2) {
                img.dataset.retry = String(tries + 1);
                const src = img.src;
                setTimeout(() => { img.src = src; }, 500 * (tries + 1));
              } else {
                img.style.display = 'none';
              }
            }}
          />
        ) : speaker.image && !shouldRenderImg ? (
          // Feb-2026 speed pass v3: below-fold placeholder — just the
          // studio-backdrop circle with the speaker's initials. Swaps
          // in the real portrait the moment IntersectionObserver fires.
          <div
            className="w-full h-full flex items-center justify-center text-xl font-serif text-white/85"
            aria-label={speaker.name}
            data-testid="speaker-portrait-placeholder"
          >
            {initials}
          </div>
        ) : (
          <span className="text-white text-xl sm:text-2xl font-bold">{initials}</span>
        )}
      </div>
      {isAdmin && !selectMode ? (
        <div className="mt-2 sm:mt-3 max-w-[7rem] sm:max-w-[9rem] w-full" onClick={(e) => e.stopPropagation()}>
          <SpeakerNameEditor
            name={speaker.name}
            onRenamed={onRenamed}
            className="text-[#0f1729] text-xs sm:text-sm text-center font-medium leading-tight truncate"
            buttonClassName="w-full justify-center hover:text-[#9a7b24] transition-colors"
          />
        </div>
      ) : (
        <p className="text-[#0f1729] text-xs sm:text-sm mt-2 sm:mt-3 text-center font-medium leading-tight max-w-[7rem] sm:max-w-[9rem]">{speaker.name}</p>
      )}
      {/* Feb-2026 (user): recording count hidden on mobile — the
          555-rec / 1571-rec numbers looked squashed under wrapped
          speaker names on narrow screens. On desktop we keep it. */}
      {isGuest && speaker.guest_recordings === 0 ? (
        <p className="text-[#9a7b24] text-[10px] sm:text-xs mt-0.5 text-center leading-tight max-w-[7rem] sm:max-w-[9rem]" data-testid="speaker-card-locked-hint">Continue with email to see</p>
      ) : (
        <p className="hidden sm:block text-[#8a8f99] text-xs mt-0.5">{isGuest && speaker.guest_recordings != null ? speaker.guest_recordings : speaker.recordings} Shiurim</p>
      )}
    </div>
  );
};

export default SpeakerCard;
